import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import styled, { css } from 'styled-components'

import useIsInStandalonePage from '@hooks/useIsInStandalonePage'
import useStandaloneViewportType from '@hooks/useStandaloneViewportType'

import { FreeRatioImageProps } from '.'
import { ActualImageDimensions } from './types'
import { generatePictureSources } from './utils'

export type ExistingProps = Pick<
  FreeRatioImageProps,
  | 'src'
  | 'alt'
  | 'crops'
  | 'loading'
  | 'className'
  | 'children'
  | 'darkModeImage'
> & {
  actualDimensions: ActualImageDimensions
  Placeholder: Required<FreeRatioImageProps>['Placeholder']
}

interface StyledPictureProps {
  hasError: boolean
}

const StyledPicture = styled.picture<StyledPictureProps>`
  ${({ hasError }) => {
    if (hasError) {
      return css`
        display: none;
      `
    }

    return css`
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;

      > img {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
      }
    `
  }}
`

const Existing: FunctionComponent<ExistingProps> = ({
  src,
  alt,
  crops,
  loading,
  Placeholder,
  className,
  children,
  actualDimensions,
}) => {
  const isInStandalonePage = useIsInStandalonePage()
  const standaloneViewportType = useStandaloneViewportType()
  const [error, setError] = useState<boolean>(false)
  const imageRef = useRef<HTMLImageElement>(null)

  const onError = useCallback(() => {
    setError(true)
  }, [])

  useEffect(() => {
    const currentImage = imageRef.current
    currentImage?.addEventListener('error', onError)
    return () => {
      currentImage?.removeEventListener('error', onError)
    }
  }, [onError])

  const {
    desktop: { width: desktopWidth },
    tablet: { width: tabletWidth },
    mobile: { width: mobileWidth },
    mobileSmall: { width: mobileSmallWidth },
  } = actualDimensions

  const {
    desktop: {
      mediaQuery: mediaQueryDesktop,
      dpr1: pictureSourceDesktopDPR1,
      dpr2: pictureSourceDesktopDPR2,
      dpr3: pictureSourceDesktopDPR3,
    },
    tablet: {
      mediaQuery: mediaQueryTablet,
      dpr1: pictureSourceTabletDPR1,
      dpr2: pictureSourceTabletDPR2,
      dpr3: pictureSourceTabletDPR3,
    },
    mobileRegular: {
      mediaQuery: mediaQueryMobileRegular,
      dpr1: pictureSourceMobileRegularDPR1,
      dpr2: pictureSourceMobileRegularDPR2,
      dpr3: pictureSourceMobileRegularDPR3,
    },
    mobileSmall: {
      mediaQuery: mediaQueryMobileSmall,
      dpr1: pictureSourceMobileSmallDPR1,
      dpr2: pictureSourceMobileSmallDPR2,
      dpr3: pictureSourceMobileSmallDPR3,
    },
  } = generatePictureSources({
    src,
    crops,
    widths: {
      desktop: desktopWidth,
      tablet: tabletWidth,
      mobile: mobileWidth,
      mobileSmall: mobileSmallWidth,
    },
  })

  const pictureSourceStandaloneDPR1 =
    standaloneViewportType === 'desktop'
      ? pictureSourceDesktopDPR1
      : standaloneViewportType === 'tablet'
        ? pictureSourceTabletDPR1
        : pictureSourceMobileRegularDPR1
  const pictureSourceStandaloneDPR2 =
    standaloneViewportType === 'desktop'
      ? pictureSourceDesktopDPR2
      : standaloneViewportType === 'tablet'
        ? pictureSourceTabletDPR2
        : pictureSourceMobileRegularDPR2
  const pictureSourceStandaloneDPR3 =
    standaloneViewportType === 'desktop'
      ? pictureSourceDesktopDPR3
      : standaloneViewportType === 'tablet'
        ? pictureSourceTabletDPR3
        : pictureSourceMobileRegularDPR3

  return (
    <Placeholder dimensions={actualDimensions} className={className}>
      <StyledPicture hasError={error}>
        {isInStandalonePage ? (
          <source
            media="all"
            srcSet={`${pictureSourceStandaloneDPR1} 1x, ${pictureSourceStandaloneDPR2} 2x, ${pictureSourceStandaloneDPR3} 3x`}
          />
        ) : (
          <>
            <source
              media={mediaQueryDesktop}
              srcSet={`${pictureSourceDesktopDPR1} 1x, ${pictureSourceDesktopDPR2} 2x, ${pictureSourceDesktopDPR3} 3x`}
            />
            <source
              media={mediaQueryTablet}
              srcSet={`${pictureSourceTabletDPR1} 1x, ${pictureSourceTabletDPR2} 2x, ${pictureSourceTabletDPR3} 3x`}
            />
            <source
              media={mediaQueryMobileRegular}
              srcSet={`${pictureSourceMobileRegularDPR1} 1x, ${pictureSourceMobileRegularDPR2} 2x, ${pictureSourceMobileRegularDPR3} 3x`}
            />
            <source
              media={mediaQueryMobileSmall}
              srcSet={`${pictureSourceMobileSmallDPR1} 1x, ${pictureSourceMobileSmallDPR2} 2x, ${pictureSourceMobileSmallDPR3} 3x`}
            />
          </>
        )}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          ref={imageRef}
          alt={alt}
          width={actualDimensions['desktop']['width']}
          height={actualDimensions['desktop']['height']}
          loading={loading}
        />
      </StyledPicture>
      {children}
    </Placeholder>
  )
}

export default Existing
